import type { SxProps, Theme } from "@mui/material/styles"
import { boxShadowElevation2 } from "./Styles"

type Style = SxProps<Theme>
type StyleFunc = <T extends Theme>(theme: T) => SxProps<T>

export const composeStyles =
  <T extends Theme>() =>
  (_theme: T): SxProps<T> => ({})

export const headerHeight = 72

export const multiLineNumRows = 6
export const smallMultiLineNumRows = 3

export const linkedinFill = "#0077b5"
export const facebookFill = "#3C5A99"

export const point93 = 0.93 // For opacity
export const point65 = 0.65 // For opacity

export const appContentWidth = 1400

export const heroIconsSize = (theme: Theme) => theme.spacing(10) // 80px
// mobile hero icon which scales heroIconsSize by 0.75
export const mobileHeroIconsSize = (theme: Theme) => theme.spacing(7.5) // 60px

export const barChartHeight = (theme: Theme) => theme.spacing(40) // 320px
export const descriptionHeight = (theme: Theme) => theme.spacing(30) // 240px

// https://v5.mui.com/system/getting-started/the-sx-prop/#borders
export const borderRadius8px = {
  borderRadius: 2,
} as const // 8px
export const borderRadius4px = {
  borderRadius: 1,
} as const // 4px

export const white65 = `rgba(255, 255, 255, ${point65})` as const
export const white93 = `rgba(255, 255, 255, ${point93})` as const

export const colorWhite = { color: "white" } as const
export const colorWhite93 = { color: white93 } as const
export const colorErrorRed = { color: "error.main" } as const

export const fontWeight400 = { fontWeight: 400 } as const
export const fontWeight500 = { fontWeight: 500 } as const
export const fontWeight600 = { fontWeight: 600 } as const

export const gray50 = (theme: Theme) => theme.palette.grey["50"]
export const gray200 = (theme: Theme) => theme.palette.grey["200"]

// https://v5.mui.com/system/getting-started/the-sx-prop/#palette
export const bgColorPrimary50 = { backgroundColor: "primary.50" } as const
export const bgColorPrimary800 = { backgroundColor: "primary.800" } as const
export const backgroundGray50 = { backgroundColor: "gray.50" } as const
export const backgroundGray200 = { backgroundColor: "gray.200" } as const
export const backgroundWhite = { backgroundColor: "white" } as const
export const backgroundBlack = { backgroundColor: "black" } as const

export const fillColorPrimary500 = (theme: Theme) => ({
  // @ts-expect-error
  fill: theme.palette.primary[500],
})

export const primaryTextColor = (theme: Theme) => theme.palette.text.primary
export const secondaryTextColor = (theme: Theme) => theme.palette.text.secondary
export const disabledTextColor = (theme: Theme) => theme.palette.text.disabled

export const positionRelative = { position: "relative" } as const
export const positionAbsolute = { position: "absolute" } as const
export const positionSticky = { position: "sticky" } as const
export const positionFixed = { position: "fixed" } as const

export const widthCalc100PercentNeg240px = {
  width: "calc(100% - 240px)",
} as const
export const widthCalc100PercentNeg30px = {
  width: "calc(100% - 30px)",
} as const
export const width39Percent = { width: "39%" } as const
export const width50Percent = { width: "50%" } as const // Skeleton
export const width90Percent = { width: "90%" } as const
export const width100Percent = { width: "100%" } as const
export const width24px = { width: 24 } as const // Zip Icon
export const width64px = { width: 64 } as const
export const width72px = { width: 72 } as const
export const width80px = { width: 80 } as const
export const width120px = { width: 160 } as const // Skeleton Job Post
export const width160px = { width: 160 } as const // Logo width
export const width240px = { width: 240 } as const
export const width242px = { width: 242 } as const
export const width320px = { width: 320 } as const
export const width400px = { width: 400 } as const
export const width480px = { width: 480 } as const
export const widthInherit = { width: "inherit" } as const

export const maxWidth100Percent = { maxWidth: "100%" } as const
export const maxWidth60Percent = { maxWidth: "60%" } as const // used for listItemSecondaryAction
export const maxWidth72px = { maxWidth: 72 } as const
export const maxWidth110px = { maxWidth: 110 } as const
export const maxWidth120px = { maxWidth: 120 } as const
export const maxWidth240px = { maxWidth: 240 } as const
export const maxWidth360px = { maxWidth: 360 } as const
export const maxWidth480px = { maxWidth: 480 } as const
export const maxWidth560px = { maxWidth: 560 } as const
export const appContentMaxWidth = { maxWidth: appContentWidth } as const

export const maxHeight100VH = { maxHeight: "100vh" } as const

export const zIndexNeg100 = { zIndex: -100 } as const // BackgroundStyleBasic - map
export const zIndex100 = { zIndex: 100 } as const
export const zIndex800 = { zIndex: 800 } as const
export const zIndexAppBar = {
  zIndex: (theme: Theme) => theme.zIndex.appBar,
} as const

const avatarSmall = (theme: Theme) => theme.spacing(3) // 24px
const avatarMedium = (theme: Theme) => theme.spacing(6) // 48px
const avatarLarge = (theme: Theme) => theme.spacing(10) // 80px
const avatarExtraLarge = (theme: Theme) => theme.spacing(20) // 160px
const avatarEditorImage = (theme: Theme) => theme.spacing(40) // 320px

export const avatarSmallSize = (theme: Theme) => ({
  height: avatarSmall(theme),
  width: avatarSmall(theme),
})
export const avatarMediumSize = (theme: Theme) => ({
  height: avatarMedium(theme),
  width: avatarMedium(theme),
})
export const avatarLargeSize = (theme: Theme) => ({
  height: avatarLarge(theme),
  width: avatarLarge(theme),
})
export const avatarExtraLargeSize = (theme: Theme) => ({
  height: avatarExtraLarge(theme),
  width: avatarExtraLarge(theme),
})
export const avatarEditorImageSize = (theme: Theme) => ({
  height: avatarEditorImage(theme),
  width: avatarEditorImage(theme),
})

// https://v5.mui.com/system/getting-started/the-sx-prop/#spacing
export const margin0 = { margin: 0 } as const
export const margin16 = { margin: 2 } as const
export const marginNeg16 = { margin: -2 } as const
export const marginLeft0 = { marginLeft: 0 } as const
export const marginLeft4 = { marginLeft: 0.5 } as const
export const marginLeft8 = { marginLeft: 1 } as const
export const marginLeft16 = { marginLeft: 2 } as const
export const marginBottom0 = { marginBottom: 0 } as const
export const marginBottom8 = { marginBottom: 1 } as const
export const marginBottom16 = { marginBottom: 2 } as const
export const marginRight0 = { marginRight: 0 } as const
export const marginRight4 = { marginRight: 0.5 } as const
export const marginRight8 = { marginRight: 1 } as const
export const marginRight16 = { marginRight: 2 } as const
export const marginTop0 = { marginTop: 0 } as const
export const marginTop4 = { marginTop: 0.5 } as const
export const marginTop8 = { marginTop: 1 } as const
export const marginTop16 = { marginTop: 2 } as const
export const marginTop32 = { marginTop: 4 } as const
export const marginTopNeg8 = { marginTop: -1 } as const // For homepage header / mobile
// For homepage header / linearprogressdrawerstyle
export const marginTopNeg16 = { marginTop: -2 } as const

export const marginTop16Bottom8 = { ...marginTop16, ...marginBottom8 } as const
export const marginTop8Bottom16 = { ...marginTop8, ...marginBottom16 } as const
export const verticalMargin16 = (theme: Theme) => ({
  margin: theme.spacing(2, 0),
})
export const verticalMargin40 = (theme: Theme) => ({
  margin: theme.spacing(5, 0),
})
export const horizontalMarginNeg8 = (theme: Theme) => ({
  margin: theme.spacing(0, -1),
})
export const horizontalMargin8 = (theme: Theme) => ({
  margin: theme.spacing(0, 1),
})
export const horizontalMargin16 = (theme: Theme) => ({
  margin: theme.spacing(0, 2),
})
export const bottomBarPadding = (theme: Theme) => ({
  margin: theme.spacing(2, 1, 1),
}) // "16px 8px 8px"

export const padding0 = { padding: 0 } as const
export const padding8 = { padding: 1 } as const
export const padding16 = { padding: 2 } as const
export const padding32 = { padding: 4 } as const
export const padding40 = { padding: 5 } as const
export const paddingTop0 = { paddingTop: 0 } as const
export const paddingTop8 = { paddingTop: 1 } as const
export const paddingTop16 = { paddingTop: 2 } as const
export const paddingTop24 = { paddingTop: 3 } as const
export const paddingTop32 = { paddingTop: 4 } as const
export const paddingTop40 = { paddingTop: 5 } as const
export const paddingBottom0 = { paddingBottom: 0 } as const
export const paddingBottom8 = { paddingBottom: 1 } as const
export const paddingBottom16 = { paddingBottom: 2 } as const
export const paddingBottom24 = { paddingBottom: 3 } as const
export const paddingBottom32 = { paddingBottom: 4 } as const
export const paddingBottom40 = { paddingBottom: 5 } as const
export const paddingLeft0 = { paddingLeft: 0 } as const
export const paddingLeft8 = { paddingLeft: 1 } as const
export const paddingLeft16 = { paddingLeft: 2 } as const
export const paddingLeft24 = { paddingLeft: 3 } as const
export const paddingLeft32 = { paddingLeft: 4 } as const
export const paddingLeft40 = { paddingLeft: 5 } as const
export const paddingRight0 = { paddingRight: 0 } as const
export const paddingRight8 = { paddingRight: 1 } as const
export const paddingRight16 = { paddingRight: 2 } as const
export const paddingRight24 = { paddingRight: 3 } as const
export const paddingRight32 = { paddingRight: 4 } as const
export const paddingRight40 = { paddingRight: 5 } as const

export const paddingLeft25Percent = { paddingLeft: "25%" } as const // Map sorry style
export const paddingBottom32Left32 = {
  ...paddingBottom32,
  ...paddingLeft32,
} as const
export const subHeadlinePadding = {
  ...paddingBottom8,
  ...paddingTop16,
} as const // Dialog
export const paddingTop0Bottom0 = { ...paddingTop0, ...paddingBottom0 } as const
export const paddingVertical8 = {
  padding: [1, 0],
} as const
export const paddingVertical16 = {
  padding: [2, 0],
} as const

export const sidePadding16 = { padding: [0, 2] } as const // For content with side padding
export const padding8px16px = {
  padding: [1, 2],
} as const

// https://v5.mui.com/system/getting-started/the-sx-prop/#sizing
export const minHeight40 = { minHeight: 40 } as const
export const minHeight80 = { minHeight: 80 } as const
export const minHeight120 = { minHeight: 120 } as const
export const minHeightAuto = { minHeight: "auto" } as const
export const minHeight100Percent = { minHeight: "100%" } as const

export const height16 = { height: 16 } as const // Linear progress height
export const height32 = { height: 32 } as const // Color picker, duration line
export const height64 = { height: 64 } as const // Small homepage hero
export const height72 = { height: 72 } as const // Needed for Wavelength logo
export const height80 = { height: 80 } as const // Homepage Hero avatar
export const height86 = { height: 86 } as const // Access requests
export const height160 = { height: 160 } as const
export const height320 = { height: 320 } as const // Email quill, wavelength avatar editor
export const height100Percent = { height: "100%" } as const

export const top0 = { top: 0 } as const
export const top16 = { top: 16 } as const
export const topNeg40 = { top: -40 } as const

export const right0 = { right: 0 } as const
export const right16 = { right: 16 } as const
export const right24 = { right: 24 } as const

export const left0 = { left: 0 } as const
export const left8 = { left: 8 } as const
export const left16 = { left: 16 } as const
export const left120 = { left: 120 } as const
export const left496 = { left: 496 } as const

export const bottom0 = { bottom: 0 } as const
export const bottom40 = { bottom: 40 } as const

export const cursorPointer = { cursor: "pointer" } as const

export const backgroundSize80Percent = { backgroundSize: "80%" } as const
export const backgroundSizeCover = { backgroundSize: "cover" } as const

export const flexAuto = { flex: "auto" } as const
export const flexShrink0 = { flexShrink: 0 } as const
export const flex1 = { flex: 1 } as const
export const flex110 = { flex: "1 1 0" } as const
export const flex00 = { flex: "0 0" } as const
export const flexDirectionRowReverse = { flexDirection: "row-reverse" } as const
export const flexWrapReverse = { flexWrap: "wrap-reverse" } as const
export const flexGrow1 = { flexGrow: 1 } as const

export const overflowHidden = { overflow: "hidden" } as const
export const overflowYHidden = { overflowY: "hidden" } as const
export const overflowYScroll = { overflowY: "scroll" } as const
export const overflowXAuto = { overflowX: "auto" } as const
export const overflowXHidden = { overflowX: "hidden" } as const

export const whiteSpacePreLine = { whiteSpace: "pre-line" } as const
export const whiteSpaceNoWrap = { whiteSpace: "nowrap" } as const

export const nameStyle = {
  ...colorWhite93,
  ...paddingVertical8,
} as const
export const updateProfileStyle = {
  ...colorWhite93,
  ...marginTop8,
} as const

const rootStyles = {
  ...positionRelative,
  ...margin0,
  ...minHeight100Percent,
  ...backgroundGray50,
} as const

export const avatar = (theme: Theme) => ({
  ...backgroundWhite,
  ...height80,
  ...width80px,
  ...borderRadius4px,

  [theme.breakpoints.down("sm")]: {
    ...height64,
    ...width64px,
    ...left8,
  },
  [theme.breakpoints.down("md")]: {
    ...height64,
    ...width64px,
    ...left16,
  },
})

export const homepageHero = (theme: Theme) => ({
  ...padding16,
  ...borderRadius4px,
  ...backgroundSizeCover,
  ...positionRelative,
  [theme.breakpoints.up("md")]: marginTopNeg16,
  "&:hover": boxShadowElevation2,
})

export const homepageAdStyles = (theme: Theme) => ({
  ...padding16,
  ...borderRadius4px,
  [theme.breakpoints.up("md")]: marginTopNeg16,
  flex: 1,
  "&:hover": {
    boxShadow: "inset 0px 2px 8px rgba(0, 0, 0, 0.1)",
  },
})

export const wavelengthBackgroundImage = {
  backgroundImage: "url('/static/radio-background-2.png')",
}
export const getGradientBackground = (url: string) => ({
  backgroundImage: `linear-gradient(90deg, rgba(0, 0, 0, 0.60), rgba(0, 0, 0, 0.60)), url(${url})`,
})

export const bodyRoot = (theme: Theme) => ({
  ...flexGrow1,
  [theme.breakpoints.up("md")]: {
    ...padding16,
    ...overflowYScroll,
    ...overflowXHidden,
  },
})

export const dashboardMenuWidth = {
  width: (theme: Theme) => theme.spacing(30),
} as const

// export const maxWidthDashboardContainer = (theme: Theme) => ({
//   maxWidth: `calc(100% - ${dashboardMenuWidth + theme.spacing(2) + 2}px)`,
//   // maxWidth: `calc(100% - ${dashboardMenuWidth + getSpacing(2) + 2}px)`,
//   // maxWidth: `calc(100% - ${dashboardMenuWidth + getSpacing(2) - 120}px)`,
//   // [stylesTheme.breakpoints.down("sm")]: {
//   //   maxWidth: `calc(100% - ${dashboardMenuWidth + getSpacing(2) + 2}px)`,
//   // },
// }) // Weirdness with tables

// export const dashboardPageStyle = {
//   ...marginLeft16,
//   ...maxWidthDashboardContainer,
// }

export const headerRoot = (theme: Theme) => ({
  ...padding16,
  [theme.breakpoints.down("md")]: {
    // @ts-expect-error
    boxShadow: `inset 0px 2px ${theme.palette.primary[800]}`,
  },
})

export const pageOuterStyle = { ...maxHeight100VH, ...overflowYScroll }

export const flexCenterColumn = {
  display: "flex",
  alignItems: "center",
  flexDirection: "column",
} satisfies Style

export const flexAlignCenter = {
  display: "flex",
  alignItems: "center",
} satisfies Style

export const grow = {
  width: "100%",
  flex: 1,
} satisfies Style

export const APP_STYLES = { root: rootStyles } as const
